import React from 'react';
import { Box, Container, Divider, Typography } from '@mui/material';
import NavbarClient from '../../components/NavbarClient';
import Footer from '../../components/Footer';

const Section = ({ number, title, children }: { number: string; title: string; children: React.ReactNode }) => (
  <Box sx={{ mb: 4 }}>
    <Typography
      variant="h6"
      sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, display: 'flex', gap: 1 }}
    >
      <span style={{ color: '#06b6d4' }}>{number}.</span> {title}
    </Typography>
    {children}
  </Box>
);

const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.8, mb: 1.5 }}>
    {children}
  </Typography>
);

const Item = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#06b6d4', mt: '7px', flexShrink: 0 }} />
    <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.8 }}>
      {children}
    </Typography>
  </Box>
);

const SubItem = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', gap: 1, mb: 1, ml: 2 }}>
    <Typography variant="body2" sx={{ color: '#06b6d4', fontWeight: 700, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.8 }}>
      {children}
    </Typography>
  </Box>
);

const TermsAndConditions = () => {
  return (
    <>
      <NavbarClient />
      <Box sx={{ pt: 14, pb: 10, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
        <Container maxWidth="md">
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 4,
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                background: 'linear-gradient(135deg, #0f172a, #1e3a5f)',
                px: { xs: 3, md: 6 },
                py: 5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#06b6d4',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  display: 'block',
                  mb: 1,
                }}
              >
                WashPass
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, color: 'white', letterSpacing: '-0.02em', mb: 1 }}
              >
                Termos e Condições de Uso
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                WASH SERVIÇOS DE TECNOLOGIA LTDA. — CNPJ 52.472.237/0001-79
              </Typography>
            </Box>

            <Box sx={{ px: { xs: 3, md: 6 }, py: 5 }}>
              <Section number="1" title="Introdução">
                <P>
                  Estes Termos e Condições de Uso (&ldquo;Termos&rdquo;) constituem um contrato vinculativo
                  entre você e WASH SERVIÇOS DE TECNOLOGIA LTDA., sociedade empresária limitada,
                  inscrita no CNPJ/MF sob o nº 52.472.237/0001-79, com sede na Rua Maratona, 285,
                  apto. 104, bloco A, Vila Alexandria, São Paulo &ndash; SP, CEP 04635-041 (&ldquo;WashPass&rdquo;).
                </P>
                <P>
                  O WashPass oferece uma plataforma digital que conecta usuários a estabelecimentos
                  parceiros para a realização de serviços de lavagem automotiva, mediante planos de
                  assinatura, conforme condições e limites definidos nestes Termos.
                </P>
                <P>
                  Ao acessar, cadastrar-se ou utilizar a plataforma WashPass, o usuário declara que
                  leu, compreendeu e concorda integralmente com estes Termos.
                </P>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="2" title="Definições">
                <P>Para os fins destes Termos:</P>
                <Item><strong>Usuário:</strong> pessoa física cadastrada na plataforma WashPass.</Item>
                <Item>
                  <strong>Usuário Pessoa Física (PF):</strong> usuário que utiliza o veículo
                  exclusivamente para fins pessoais, não profissionais.
                </Item>
                <Item>
                  <strong>Motorista Profissional:</strong> usuário que utiliza o veículo de forma
                  habitual ou recorrente para fins profissionais, comerciais ou de geração de renda,
                  incluindo, mas não se limitando, a motoristas de aplicativo, táxis, entregadores e
                  frotistas.
                </Item>
                <Item>
                  <strong>Parceiros:</strong> estabelecimentos de lavagem e estética automotiva
                  credenciados à plataforma.
                </Item>
                <Item>
                  <strong>Plano:</strong> modalidade de assinatura contratada pelo usuário, com
                  regras e limites específicos.
                </Item>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="3" title="Aceitação dos Termos">
                <P>
                  O aceite destes Termos ocorre de forma eletrônica, no momento do cadastro ou da
                  contratação da assinatura, mediante ação inequívoca do usuário, sendo condição
                  indispensável para utilização da plataforma.
                </P>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="4" title="Modificações dos Termos">
                <P>
                  O WashPass poderá revisar e atualizar estes Termos periodicamente. A versão
                  vigente será disponibilizada na plataforma, e o uso continuado após a atualização
                  implicará aceitação das novas condições.
                </P>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="5" title="Descrição dos Serviços">
                <P>
                  O WashPass atua como intermediador tecnológico, não executando diretamente os
                  serviços de lavagem automotiva.
                </P>
                <P>
                  Os serviços são prestados exclusivamente pelos parceiros, que são responsáveis por
                  sua execução, qualidade, conformidade técnica e regulatória.
                </P>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="6" title="Cadastro de Usuário">
                <P>
                  O usuário deverá fornecer informações verídicas, completas e atualizadas, incluindo
                  dados pessoais e do veículo vinculado à assinatura.
                </P>
                <P>
                  Cada assinatura está vinculada a um único veículo, identificado por placa, sendo
                  vedada a utilização para veículos distintos.
                </P>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="7" title="Classificação do Usuário e Reclassificação">
                <SubItem label="7.1.">
                  O usuário declara ciência de que poderá ser classificado como Pessoa Física ou
                  Motorista Profissional, conforme a finalidade do uso do veículo e o padrão de
                  utilização da plataforma.
                </SubItem>
                <SubItem label="7.2.">
                  O WashPass poderá, a qualquer tempo, reclassificar o usuário, com base em critérios
                  objetivos, incluindo frequência de uso, recorrência, dados operacionais, validações
                  por QR Code e informações fornecidas pelos parceiros.
                </SubItem>
                <SubItem label="7.3.">
                  A utilização de plano incompatível com a classificação do usuário poderá ensejar a
                  aplicação das medidas previstas nestes Termos.
                </SubItem>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="8" title="Limites de Uso e Disponibilidade">
                <P>
                  O uso da plataforma está sujeito a limites operacionais, técnicos e geográficos,
                  bem como à disponibilidade da rede de parceiros, não havendo garantia de
                  atendimento contínuo ou imediato.
                </P>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="9" title="Política de Uso Consciente">
                <SubItem label="9.1.">
                  O WashPass adota o princípio do uso consciente, aplicável a todos os usuários.
                </SubItem>
                <SubItem label="9.2.">
                  O uso consciente consiste na realização de lavagens apenas quando o veículo estiver
                  efetivamente sujo, sendo vedado o uso excessivo, repetitivo ou incompatível com a
                  finalidade do plano contratado.
                </SubItem>
                <SubItem label="9.3.">
                  O uso em desacordo com esta política poderá ser caracterizado como mau uso, ainda
                  que não haja violação expressa de limites numéricos.
                </SubItem>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="10" title="Monitoramento, Prevenção a Fraudes e Mau Uso">
                <P>
                  O WashPass poderá monitorar padrões de uso, frequência, recorrência, validações por
                  QR Code, dados de localização e informações fornecidas por parceiros, com a
                  finalidade de:
                </P>
                <Item>Prevenir e combater fraudes;</Item>
                <Item>Identificar uso incompatível com o plano;</Item>
                <Item>Proteger a integridade e sustentabilidade da plataforma.</Item>
                <P>Caracterizam, exemplificativamente, condutas vedadas:</P>
                <Item>Utilização de plano PF por Motorista Profissional;</Item>
                <Item>Uso recorrente ou abusivo incompatível com o perfil do plano;</Item>
                <Item>Utilização da assinatura para veículos não cadastrados;</Item>
                <Item>Tentativas de burlar regras operacionais ou de validação.</Item>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="11" title="Assinaturas, Cobrança e Renovação">
                <P>
                  As condições comerciais, valores, periodicidade de cobrança, renovação automática e
                  regras de cancelamento pelo usuário serão aquelas informadas no momento da
                  contratação do plano.
                </P>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="12" title="Cancelamento da Assinatura pelo Usuário">
                <P>
                  O usuário poderá cancelar sua assinatura a qualquer momento, conforme as regras
                  disponíveis na plataforma, produzindo efeitos ao final do período vigente, salvo
                  disposição legal em contrário.
                </P>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="13" title="Cancelamento, Suspensão e Rescisão Unilateral pelo WashPass">
                <SubItem label="13.1.">
                  O WashPass poderá, a seu critério, limitar, suspender ou cancelar a conta e/ou a
                  assinatura do usuário, inclusive de forma imediata, nas seguintes hipóteses:
                </SubItem>
                <Box sx={{ ml: 4, mb: 2 }}>
                  <Item>Violação destes Termos;</Item>
                  <Item>Descumprimento da política de uso consciente;</Item>
                  <Item>Suspeita razoável de fraude ou mau uso;</Item>
                  <Item>Utilização de plano incompatível com a classificação do usuário;</Item>
                  <Item>Quebra da boa-fé contratual.</Item>
                </Box>
                <SubItem label="13.2.">
                  O cancelamento por tais motivos não gera direito a indenização ou reembolso,
                  ressalvadas as hipóteses previstas em lei.
                </SubItem>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="14" title="Propriedade Intelectual">
                <P>
                  Todo o conteúdo, marca, tecnologia e funcionalidades da plataforma WashPass são
                  protegidos por direitos de propriedade intelectual, sendo vedada qualquer utilização
                  não autorizada.
                </P>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="15" title="Proteção de Dados Pessoais">
                <P>
                  O tratamento de dados pessoais observará integralmente o disposto na Política de
                  Privacidade do WashPass, parte integrante destes Termos.
                </P>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="16" title="Limitação de Responsabilidade">
                <P>
                  O WashPass não se responsabiliza pela execução dos serviços prestados pelos
                  parceiros, respondendo exclusivamente pela disponibilização da plataforma
                  tecnológica, nos limites da legislação aplicável.
                </P>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="17" title="Foro">
                <P>
                  Fica eleito o Foro da Comarca de São Paulo – SP, ressalvado o direito do consumidor
                  de optar pelo foro de seu domicílio, quando aplicável.
                </P>
              </Section>

              <Divider sx={{ mb: 4 }} />

              <Section number="18" title="Disposições Gerais">
                <P>
                  A eventual tolerância não implica renúncia de direitos. A invalidade de qualquer
                  cláusula não afetará as demais.
                </P>
              </Section>

              <Box
                sx={{
                  mt: 4,
                  p: 2.5,
                  borderRadius: 2,
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                  WASH SERVIÇOS DE TECNOLOGIA LTDA. — CNPJ 52.472.237/0001-79
                  <br />
                  Rua Maratona, 285, apto. 104, bloco A, Vila Alexandria, São Paulo – SP, CEP 04635-041
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default TermsAndConditions;
